import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Trash2, ArrowLeft, Save, Edit, GripVertical } from 'lucide-react';
import { coursesApi, chaptersApi, courseAuthoringApi } from '../../lib/api';
import { toast } from 'sonner';

export const CourseAuthoringDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chapters');
  const [editingChapter, setEditingChapter] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showAddChapter, setShowAddChapter] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [newChapter, setNewChapter] = useState({ title: '', content: '', starter_code: '' });
  const [newQuestion, setNewQuestion] = useState({ question: '', options: ['', '', '', ''], correct_answer: 0 });

  useEffect(() => {
    fetchData();
  }, [courseId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [courseData, chaptersData, examData] = await Promise.all([
        coursesApi.getById(courseId),
        chaptersApi.getByCourse(courseId),
        courseAuthoringApi.getCourseExam(courseId).catch(() => null)
      ]);
      setCourse(courseData);
      setChapters(chaptersData);
      setExam(examData);
    } catch (error) {
      toast.error('Failed to load course data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChapter = async (chapterId) => {
    try {
      await courseAuthoringApi.updateChapter(chapterId, editingChapter);
      toast.success('Chapter saved');
      setEditingChapter(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save chapter');
    }
  };

  const handleAddChapter = async () => {
    try {
      await courseAuthoringApi.createChapter(courseId, newChapter);
      toast.success('Chapter added');
      setShowAddChapter(false);
      setNewChapter({ title: '', content: '', starter_code: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add chapter');
    }
  };

  const handleDeleteChapter = async (chapterId) => {
    if (!window.confirm('Delete this chapter?')) return;
    try {
      await courseAuthoringApi.deleteChapter(chapterId);
      toast.success('Chapter deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete chapter');
    }
  };

  const handleAddQuestion = async () => {
    try {
      await courseAuthoringApi.addExamQuestion(courseId, newQuestion);
      toast.success('Question added');
      setShowAddQuestion(false);
      setNewQuestion({ question: '', options: ['', '', '', ''], correct_answer: 0 });
      fetchData();
    } catch (error) {
      toast.error('Failed to add question');
    }
  };

  const handleDeleteQuestion = async (index) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await courseAuthoringApi.deleteExamQuestion(courseId, index);
      toast.success('Question deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  if (loading) return <div className="skeleton h-64 rounded-lg" />;

  return (
    <div className="space-y-6" data-testid="course-authoring-detail">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/admin/course-authoring')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{course?.title}</h1>
          <p className="text-muted-foreground">{course?.description}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button onClick={() => setActiveTab('chapters')} className={`px-4 py-2 border-b-2 ${activeTab === 'chapters' ? 'border-primary text-primary' : 'border-transparent'}`}>
          Chapters ({chapters.length})
        </button>
        <button onClick={() => setActiveTab('exam')} className={`px-4 py-2 border-b-2 ${activeTab === 'exam' ? 'border-primary text-primary' : 'border-transparent'}`}>
          Exam Questions ({exam?.questions?.length || 0})
        </button>
      </div>

      {/* Chapters Tab */}
      {activeTab === 'chapters' && (
        <div className="space-y-4">
          <Button onClick={() => setShowAddChapter(true)}><Plus className="w-4 h-4 mr-2" /> Add Chapter</Button>
          
          {chapters.map((ch, idx) => (
            <div key={ch.chapter_id} className="p-4 rounded-lg bg-card border border-border">
              {editingChapter?.chapter_id === ch.chapter_id ? (
                <div className="space-y-3">
                  <Input value={editingChapter.title} onChange={e => setEditingChapter({...editingChapter, title: e.target.value})} placeholder="Title" />
                  <Textarea value={editingChapter.content} onChange={e => setEditingChapter({...editingChapter, content: e.target.value})} placeholder="Content (Markdown)" rows={8} />
                  {course?.has_code_lab && (
                    <Textarea value={editingChapter.starter_code || ''} onChange={e => setEditingChapter({...editingChapter, starter_code: e.target.value})} placeholder="Starter Code" rows={4} />
                  )}
                  <div className="flex gap-2">
                    <Button onClick={() => handleSaveChapter(ch.chapter_id)}><Save className="w-4 h-4 mr-2" /> Save</Button>
                    <Button variant="outline" onClick={() => setEditingChapter(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold">{ch.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{ch.content?.substring(0, 150)}...</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditingChapter(ch)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteChapter(ch.chapter_id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Exam Tab */}
      {activeTab === 'exam' && (
        <div className="space-y-4">
          <Button onClick={() => setShowAddQuestion(true)}><Plus className="w-4 h-4 mr-2" /> Add Question</Button>
          
          {exam?.questions?.map((q, idx) => (
            <div key={idx} className="p-4 rounded-lg bg-card border border-border">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-medium">Q{idx + 1}: {q.question}</div>
                  <div className="mt-2 space-y-1">
                    {q.options.map((opt, oi) => (
                      <div key={oi} className={`text-sm ${oi === q.correct_answer ? 'text-green-500 font-medium' : 'text-muted-foreground'}`}>
                        {oi === q.correct_answer ? '✓' : '○'} {opt}
                      </div>
                    ))}
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteQuestion(idx)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Chapter Dialog */}
      <Dialog open={showAddChapter} onOpenChange={setShowAddChapter}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Add Chapter</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input value={newChapter.title} onChange={e => setNewChapter({...newChapter, title: e.target.value})} placeholder="Chapter Title" />
            <Textarea value={newChapter.content} onChange={e => setNewChapter({...newChapter, content: e.target.value})} placeholder="Content (Markdown)" rows={8} />
            {course?.has_code_lab && (
              <Textarea value={newChapter.starter_code} onChange={e => setNewChapter({...newChapter, starter_code: e.target.value})} placeholder="Starter Code" rows={4} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddChapter(false)}>Cancel</Button>
            <Button onClick={handleAddChapter}>Add Chapter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Question Dialog */}
      <Dialog open={showAddQuestion} onOpenChange={setShowAddQuestion}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Add Question</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input value={newQuestion.question} onChange={e => setNewQuestion({...newQuestion, question: e.target.value})} placeholder="Question" />
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="flex items-center gap-2">
                <input type="radio" name="correct" checked={newQuestion.correct_answer === i} onChange={() => setNewQuestion({...newQuestion, correct_answer: i})} />
                <Input value={newQuestion.options[i]} onChange={e => {
                  const opts = [...newQuestion.options];
                  opts[i] = e.target.value;
                  setNewQuestion({...newQuestion, options: opts});
                }} placeholder={`Option ${i + 1}`} />
              </div>
            ))}
            <p className="text-sm text-muted-foreground">Select the correct answer by clicking the radio button.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddQuestion(false)}>Cancel</Button>
            <Button onClick={handleAddQuestion}>Add Question</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Admin Dashboard Component
